import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Share2 } from "lucide-react";
import Button from "./Button";
import ShareResultsForm from "./ShareResultsForm";
import { useErrorHandling } from "../hooks/useErrorHandling";
import FeedbackToast from "./FeedbackToast";
import { ValueArchetypeHeatmap } from "./ValueArchetypeHeatmap";
import CryptoJS from "crypto-js";
import { supabase } from "../lib/supabase";

interface PremiumReportProps {
  archetype: string;
  content: {
    fullTitle: string;
    intro: string;
    careerRoles: string[];
    strengths: string[];
    quicktips: string;
    growthAreasIntro: string;
    growthAreas: string[];
    microCoachingTips: string;
    description: string;
    personalizeRoadMap: {
      initial: string[];
      midterm: string[];
      longterm: string[];
      tips: string;
    };
    careerAdvice: string;
    shortintro: string;
    archnarrative: string;
    rolesAvoidIntro: string;
    rolesAvoid: string[];
    prefferedWork: {
      intro: string;
      need: string[];
      tip: string;
    };
    idealEnvironment: {
      need: string[];
      story: string;
    };
    aiPrompts: {
      title: string;
      intro: string;
      need: string[];
      tip: string;
    };
    affirmation: string;
    latinovalues: {
      intro: string;
      values: string[];
    };
    powerQuote: string;
  };
  usersName?: string;
}

const PremiumReport: React.FC<PremiumReportProps> = ({
  archetype,
  content,
  usersName = "",
}) => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [categoryScores, setCategoryScores] = React.useState<any>({});
  const { error, handleError, clearError } = useErrorHandling();
  const [percentages, setPercentages] = useState<Record<string, number>>({});
  const [firstName, setFirstName] = useState<string>("");
  // Add state for showing the notice
  const [showShareNotice, setShowShareNotice] = useState(false);
  const [usersNewName, setUsersNewName] = useState<string>(usersName);
  const [testResultId, setTestResultId] = useState<string>("");

  useEffect(() => {
    const verifyPayment = async () => {
      // get the encrypted value from the URL
      const searchParams = new URLSearchParams(window.location.search);
      const encryptedValue = searchParams.get("encrypted") || "";
      const secretKey =
        import.meta.env.VITE_SECRET_KEY || "your-strong-secret-key"; // Store securely

      // The following lines reference CryptoJS and secretKey, which are not defined in this file.
      // Uncomment and define them if needed, or remove if not used.
      const bytes = CryptoJS.AES.decrypt(encryptedValue, secretKey);
      const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      // console.log("Decrypted data:", decryptedData);
      const customerEmail = decryptedData.recipientEmail || "";

      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("*")
        .eq("email", customerEmail)
        .single();

      // console.log("Fetched User Data:", userData);
      setFirstName(userData.first_name || "");
      setUsersNewName(userData.first_name + " " + userData.last_name || "");

      if (userError) throw userError;

      // Get the latest test result for this user
      const { data: testResult, error: testError } = await supabase
        .from("test_results")
        .select("*")
        .eq("user_id", userData.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (testError) throw testError;

      setTestResultId(testResult.id || "");
      setIsLoading(false);
      setPercentages(testResult.score_data || {});
      setCategoryScores(testResult.categoryScores || {});
    };
    verifyPayment().catch((error) => {
      console.error("Error verifying payment:", error);
      handleError(error, "Verify Payment");
    });
  }, [handleError, searchParams]);

  if (!content || !archetype) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Error Loading Premium Report
        </h2>
        <p className="text-gray-600 mb-4">
          We encountered an issue loading your premium report. Please try
          refreshing the page or contact support at support@latinocareertest.com
        </p>
      </div>
    );
  }

  const handleDownloadPDF = () => {
    try {
      window.print();
    } catch (err) {
      handleError(err, "Print Results");
    }
  };

  const handleTitle = (archetype: string) => {
    switch (archetype) {
      case "colibri":
        return usersName + " es un Colibrí – La Chispa Alegre";
      case "condor":
        return usersName + " es un Cóndor – El Estratega Visionario";
      case "jaguar":
        return usersName + " es un Jaguar – El Triunfador Decidido";
      case "llama":
        return usersName + " es una Llama – El Colaborador Leal";
      case "quetzal":
        return usersName + " es un Quetzal – El Creativo Auténtico";
      case "coqui":
        return usersName + " es un Coquí – El Defensor de la Comunidad";
      case "buho":
        return usersName + " es un Búho – El Planificador Analítico";
      default:
        return usersName + " es un " + archetype + " - El arquetipo único";
    }
  };

  // Convert raw score to 1-10 scale with one decimal place precision
  const getArchetypeScore = (percentage: number): number => {
    return Number(((percentage / 100) * 9 + 1).toFixed(1));
  };

  const handleShareLinkedIn = () => {
    const capitalizedArchetype =
    archetype.charAt(0).toUpperCase() + archetype.slice(1);
    const shareUrl = 'https://testlatino.com';
    const shareText = `Descubrí que soy un ${capitalizedArchetype} en el Test Latino™. Descubre qué arquetipo eres, tomando el test gratis.`;
    const linkedInText = shareText + " " + shareUrl
    navigator.clipboard.writeText(linkedInText);

    // Show the notice
    setShowShareNotice(true);

    // Redirect after 3 seconds
    setTimeout(() => {
      setShowShareNotice(false);
      const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite`;
      window.open(
       linkedinUrl,
        "_blank"
      );
    }, 3000);
  };

  // Add this before your return statement, after the loading check

  const handleShareSuccess = () => {
    clearError();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando su compra...</p>
        </div>
      </div>
    );
  }

  // console.log("Premium Report Content:", content);
  // console.log("Premium Report Archetype:", archetype);

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          {handleTitle(archetype)}
        </h2>

        <section className="mb-8">
          <p
            className="text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content.intro }}
          />
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            🔹 Descripción breve para {firstName}:
          </h3>
          <p
            className="text-gray-700"
            dangerouslySetInnerHTML={{ __html: content.shortintro }}
          />
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            ✨ Narrativa del Arquetipo para {firstName}
          </h3>
          <p
            className="text-gray-700 mb-4"
            dangerouslySetInnerHTML={{ __html: content.archnarrative }}
          />
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            📊 Fortalezas del {content.fullTitle}
          </h3>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.strengths.map((strength, index) => (
              <li key={index} className="text-gray-700">
                {strength}
              </li>
            ))}
          </ul>
          <p className="text-gray-700 mb-4">
            <i>
              <b>Consejo rápido:</b>{" "}
              <span
                dangerouslySetInnerHTML={{ __html: content.quicktips }}
              ></span>
            </i>
          </p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            ⬆️ Áreas de Crecimiento para {firstName} y Cómo Mejorar
          </h3>
          <p className="text-gray-700 mb-4">{content.growthAreasIntro}</p>

          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.growthAreas.map((growthArea, index) => (
              <li
                key={index}
                className="text-gray-700"
                dangerouslySetInnerHTML={{ __html: growthArea }}
              ></li>
            ))}
          </ul>
          <p className="text-gray-700 mb-4">
            <i>
              <b>Microconsejo:</b>{" "}
              <span
                dangerouslySetInnerHTML={{ __html: content.microCoachingTips }}
              ></span>
            </i>
          </p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            💼 Consejos de Carrera para {firstName}
          </h3>
          <div
            className="mb-4"
            dangerouslySetInnerHTML={{ __html: content.careerAdvice }}
          ></div>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            📅 Hoja de Ruta Profesional de {usersName}
          </h3>
          <h5 className="text-gray-700 mb-4 text-bold">0–3 Meses</h5>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.personalizeRoadMap.initial.map((initials, index) => (
              <li key={index} className="text-gray-700">
                {initials}
              </li>
            ))}
          </ul>
          <h5 className="text-gray-700 mb-4 text-bold">3–6 Meses</h5>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.personalizeRoadMap.midterm.map((initials, index) => (
              <li key={index} className="text-gray-700">
                {initials}
              </li>
            ))}
          </ul>
          <h5 className="text-gray-700 mb-4 text-bold">6–12 Meses</h5>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.personalizeRoadMap.longterm.map((initials, index) => (
              <li key={index} className="text-gray-700">
                {initials}
              </li>
            ))}
          </ul>
          <p className="text-gray-700 mb-4">
            <i>
              <b>Consejo rápido:</b>{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: content.personalizeRoadMap.tips,
                }}
              ></span>
            </i>
          </p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            🎓 Roles Laborales Ideales para {firstName}
          </h3>
          <ul className="list-disc pl-5 space-y-2">
            {content.careerRoles.map((role, index) => (
              <li key={index} className="text-gray-700">
                {role}
              </li>
            ))}
          </ul>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            ⛔ Roles a Evitar
          </h3>
          <p
            className="text-gray-700 mb-4"
            dangerouslySetInnerHTML={{ __html: content.rolesAvoidIntro }}
          ></p>
          <ul className="list-disc pl-5 space-y-2">
            {content.rolesAvoid.map((roleAvoid, index) => (
              <li key={index} className="text-gray-700">
                {roleAvoid}
              </li>
            ))}
          </ul>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            🚀 Estilo de Trabajo Preferido de {firstName}
          </h3>
          <p
            className="text-gray-700 mb-4"
            dangerouslySetInnerHTML={{ __html: content.prefferedWork.intro }}
          ></p>
          <p className="text-gray-700 mb-4"> <b>Necesitas: </b></p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.prefferedWork.need.map((needs, index) => (
              <li key={index} className="text-gray-700">
                {needs}
              </li>
            ))}
          </ul>
          <p className="text-gray-700 mb-4">
            {" "}
            <i>
              <b>Microconsejo: </b>{" "}
              <span
                dangerouslySetInnerHTML={{ __html: content.prefferedWork.tip }}
              ></span>
            </i>
          </p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            🏙 Entorno Ideal para {firstName}
          </h3>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.idealEnvironment.need.map((needs, index) => (
              <li key={index} className="text-gray-700">
                {needs}
              </li>
            ))}
          </ul>
          <p className="text-gray-700 mb-4">
            {" "}
            <i>
              <b>Historia de Éxito:</b>{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: content.idealEnvironment.story,
                }}
              ></span>
            </i>
          </p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            ✨ Prompts de IA para el Crecimiento del {content.fullTitle}
          </h3>
          <p className="text-gray-700 mb-4">{content.aiPrompts.intro}</p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.aiPrompts.need.map((prompt, index) => (
              <li key={index} className="text-gray-700">
                {prompt}
              </li>
            ))}
          </ul>
          <p className="text-gray-700 mb-4">
            <i>
              <b>Tip Pro:</b>{" "}
              <span
                dangerouslySetInnerHTML={{ __html: content.aiPrompts.tip }}
              ></span>
            </i>
          </p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            🌟 Afirmación para un {content.fullTitle}
          </h3>
          <p className="text-gray-700 mb-4">{content.affirmation}</p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            🌿 Alineación de {firstName} con Valores Latinos
          </h3>
          <p className="text-gray-700 mb-4">
            Tu arquetipo se alinea fuertemente con:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {content.latinovalues.values.map((value, index) => (
              <li key={index} className="text-gray-700">
                {value}
              </li>
            ))}
          </ul>
          <p
            className="text-gray-700 mb-4"
            dangerouslySetInnerHTML={{ __html: content.latinovalues.intro }}
          ></p>
        </section>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8">
          <blockquote
            className="border-l-4 border-orange-500 pl-4 italic"
            dangerouslySetInnerHTML={{ __html: content.powerQuote }}
          ></blockquote>
        </section>

        <div className="mb-12 no-print">
          <div className="bg-white rounded-lg">
            <h3 className="text-lg font-semibold mb-4">
              Desglose de tu puntuacion para cada Arquetipo Latino™
            </h3>

            <div className="space-y-4">
              {Object.entries(percentages)
                .sort((a, b) => b[1] - a[1]) // Sort descending by percentage
                .map(([archetype, percentage]) => {
                  const score = getArchetypeScore(percentage);
                  const isTopScore =
                    Math.abs(
                      percentage - Math.max(...Object.values(percentages))
                    ) < 0.1;

                  return (
                    <div key={archetype}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium capitalize">
                          {archetype === "condor"
                            ? "Cóndor"
                            : archetype === "colibri"
                            ? "Colibri"
                            : archetype === "coqui"
                            ? "Coquí"
                            : archetype === "buho"
                            ? "Búho"
                            : archetype.charAt(0).toUpperCase() +
                              archetype.slice(1)}
                        </span>
                        <span className="text-sm font-medium flex items-center">
                          {score.toFixed(1)}
                          {isTopScore && (
                            <span className="ml-1 text-xs text-orange-500">
                              ★
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor:
                              archetype === "jaguar"
                                ? "#E05D25"
                                : archetype === "condor"
                                ? "#37474f"
                                : archetype === "llama"
                                ? "#C19A6B"
                                : archetype === "quetzal"
                                ? "#218380"
                                : archetype === "colibri"
                                ? "#73D2DE"
                                : archetype === "coqui"
                                ? "#00796b"
                                : "#455a64", // buho
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        <div className="mb-12 no-print">
          <ValueArchetypeHeatmap categoryScores={categoryScores} />
        </div>

        <hr className="my-8 border-gray-700" />

        <section className="mb-8 text-center">
          <p className="text-gray-700 mb-4">
            El <b>Latino Test™</b> es una herramienta culturalmente diseñada para la autoconciencia y la navegación profesional.
          </p>
          <p className="text-gray-700 mb-4">
            Creado por{" "}
            <a className="text-blue-700" href="https://www.linkedin.com/in/josepinero"
                  target="_blank"
                  rel="noopener noreferrer">José Piñero</a>, El Top Coach para Latinos
          </p>
          <p className="text-gray-700 mb-4">
            Hecho con amor para la comunidad profesional latina
          </p>
          <p className="text-gray-700 text-sm mb-3">
            © 2025 YOJ Inc. Todos los derechos reservados.
          </p>
          <p className="text-gray-700 text-sm mb-3">
            <b>Latino Test™ es una marca registrada de YOJ Inc. </b> Todas las demás marcas son propiedad de sus respectivos dueños.
          </p>
          <p className="text-gray-700 text-sm mb-3">
            El <b>Latino Test™</b> es propiedad intelectual de YOJ Inc. y fue desarrollado por José Piñero, ampliamente reconocido como el Top Coach para Latinos.
          </p>
          <p className="text-gray-700 text-sm mb-3">
            <b>Latino Leadership Performance</b> es una división comercial de YOJ Inc.
          </p>
          <p className="text-gray-700 text-sm mb-3">
            La reproducción, distribución o uso no autorizado de este test, sus preguntas, arquetipos o contenido relacionado está estrictamente prohibido y protegido por leyes de derechos de autor y marcas registradas en EE. UU.
          </p>
          <p className="text-gray-700 text-sm mb-3">
            <a
              href="https://www.latinoleadership.net"
              target="_blank"
              className="text-blue-700"
            >
              www.latinoleadership.net
            </a>
          </p>
        </section>

        <div className="flex gap-4 mt-8 no-print">
          <Button onClick={handleDownloadPDF} className="flex items-center">
            <Download size={18} className="mr-2" />
            Descargar PDF
          </Button>

          <Button
            variant="outline"
            onClick={handleShareLinkedIn}
            className="flex items-center"
          >
            <Share2 size={18} className="mr-2" />
            Compartir en LinkedIn
          </Button>
        </div>
      </div>
      <div className="mb-12" id="share-results-block">
        <ShareResultsForm
          testResultId={testResultId}
          inviterName={usersNewName || ""}
          archetype={archetype}
          archetypeDescription={content.description}
          onShare={handleShareSuccess}
        />
      </div>
      {error && (
        <FeedbackToast message={error} type="error" onClose={clearError} />
      )}
      {showShareNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Your Results Are Ready to Be Shared on social media!
            </h3>
            <p className="text-gray-600">
              Your test results content has been created and copied. <br />
              Just open (LinkedIn, Facebook), open a new post, hit “paste”, and
              post.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PremiumReport;
